import styled from '@emotion/styled';
import React from 'react';
import {
  EDynamicTooltip,
  EIconSmallButton,
  ETooltip,
} from 'webapp-lib/Components';
import {ContextAction, useContextMenu} from 'webapp-lib/Functions';
import {
  mixinSVGIcon,
  mixinSVGIconMedium,
  mixinTruncate,
} from 'webapp-lib/styling';
import {classy} from 'webapp-lib/utils';

import {
  CasusIcon,
  ChevronSmallDown,
  ChevronSmallRight,
  Delete,
  Empty,
  FilingCabinet,
  Folder as FolderIcon,
  Info,
} from '_/assets/images';
import {memo} from '_/utilities/memo';

import {Folder, FolderIconType} from './Folder';

export type FolderTreeItemProps = {
  folder: Folder;
  isSelected?: boolean;
  isDragging?: boolean;
  isDraggingOver?: boolean;
  actions: ContextAction<Folder>[];
  customToolTipText?: string;
  showIconOnly: boolean;
  onSelected: (folder: Folder) => void;
  onExpand: (folder: Folder) => Promise<void>;
  onCollapse: (folder: Folder) => void;
} & React.DetailedHTMLProps<
  React.HTMLAttributes<HTMLDivElement>,
  HTMLDivElement
>;

const UnstyledFolderTreeItem = ({
  folder,
  isSelected,
  isDragging,
  isDraggingOver = false,
  actions,
  customToolTipText,
  showIconOnly,
  onSelected,
  onCollapse,
  onExpand,
  ...htmlProps
}: FolderTreeItemProps): React.ReactElement => {
  const {contextMenuCallbacks, contextMenu} = useContextMenu(actions, folder);

  const handleClick = () => {
    if (folder.isNotSelectable) {
      return;
    }

    if (!folder.isExpanded && folder.hasSubFolders) {
      onExpand(folder);
    }
    onSelected(folder);
  };
  const handleChevronClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    if (folder.isExpanded) {
      onCollapse(folder);
    } else {
      onExpand(folder);
    }
  };
  const handleContextMenu = (event: React.MouseEvent) => {
    if (!isSelected) {
      handleClick();
    }
    contextMenuCallbacks.onContextMenu(event);
  };

  const classes = classy({
    selected: isSelected || false,
    disabled: folder.isDisabled,
    'extra-space-left': !folder.hasSubFolders && folder.parent !== undefined,
    'is-dragging': isDragging || false,
    'is-dragging-over': folder.isDropDisabled ? false : isDraggingOver,
    'not-selectable': folder.isNotSelectable,
  });

  return (
    <div
      {...htmlProps}
      aria-disabled={folder.isDisabled}
      aria-selected={isSelected}
      className={`${htmlProps.className} ${classes}`}
      data-testid={`FolderTreeItem-${folder.id}`}
      role='option'
      tabIndex={0}
      onClick={handleClick}
      onContextMenu={handleContextMenu}
      onKeyDown={htmlProps.onKeyDown}
    >
      <ChevronElement folder={folder} onChevronClick={handleChevronClick} />
      {showIconOnly ? (
        <ETooltip title={customToolTipText ?? folder.name}>
          <IconWrapper>
            <Icon iconType={folder.iconType} />
          </IconWrapper>
        </ETooltip>
      ) : (
        <>
          <IconWrapper>
            <Icon iconType={folder.iconType} />
          </IconWrapper>
          {customToolTipText ? (
            <ETooltip title={customToolTipText}>
              <TextWrapper>{folder.name}</TextWrapper>
            </ETooltip>
          ) : (
            <EDynamicTooltip title={folder.name}>
              <TextWrapper>{folder.name}</TextWrapper>
            </EDynamicTooltip>
          )}
          {folder.infoText ? (
            <ETooltip title={folder.infoText}>
              <IconWrapper className='info-icon'>
                <Info />
              </IconWrapper>
            </ETooltip>
          ) : null}
        </>
      )}

      {contextMenu}
    </div>
  );
};

const ChevronElement = ({
  folder,
  onChevronClick,
}: {
  folder: Folder;
  onChevronClick: (event: React.MouseEvent<HTMLButtonElement>) => void;
}): React.ReactElement | null => {
  if (!folder.isExpandable) {
    return null;
  }

  if (!folder.hasSubFolders) {
    if (!folder.isRoot) {
      return null;
    }
    return (
      <IconWrapper className='chevron-placeholder'>
        <Empty />
      </IconWrapper>
    );
  }

  return (
    <EIconSmallButton
      disabled={folder.isExpansionChevronDisabled}
      onClick={onChevronClick}
    >
      {folder.isExpanded ? <ChevronSmallDown /> : <ChevronSmallRight />}
    </EIconSmallButton>
  );
};

const Icon = ({iconType}: {iconType: FolderIconType}): React.ReactElement => {
  switch (iconType) {
    case FolderIconType.Casus:
      return <CasusIcon />;
    case FolderIconType.Trash:
      return <Delete />;
    case FolderIconType.Folder:
      return <FolderIcon />;
    case FolderIconType.AllItems:
      return <FilingCabinet />;
    case FolderIconType.Justitia:
      return <CasusIcon />; // TODO LEXIAN-6937
    case FolderIconType.JustitiaSubmission:
      return <FolderIcon />; // TODO LEXIAN-6937
  }
};

const IconWrapper = styled.span`
  margin-right: var(--gutter-little);
  display: flex;
  align-items: center;

  svg {
    ${mixinSVGIcon};
  }

  &.info-icon {
    color: var(--color-core-primary);
    margin-left: auto;
  }

  &.chevron-placeholder {
    margin: var(--gutter-very-tiny);

    svg {
      ${mixinSVGIconMedium};
    }
  }
`;

export const TextWrapper = styled.span`
  ${mixinTruncate};
`;

export const TREE_ITEM_HEIGHT = 34;
const PLACEHOLDER_SPACE_FOR_ICON = 28;
const TREE_INDENTATION = 12;

export const FolderTreeItem = styled(memo(UnstyledFolderTreeItem))<{
  isSelected: boolean;
  folder: Folder;
  level: number;
}>`
  display: flex;
  align-items: center;
  min-height: ${TREE_ITEM_HEIGHT}px;

  font-size: var(--font-size-small);
  font-weight: var(--font-weight-thin);
  color: var(--color-core-link);
  cursor: pointer;

  padding: var(--gutter-tiny);
  ${({level}): string => `padding-left: ${(level + 1) * TREE_INDENTATION}px;`};

  &.folder-color-black svg {
    color: var(--folder-color-black);
  }

  &.folder-color-orange svg {
    color: var(--folder-color-orange);
  }

  &.folder-color-green svg {
    color: var(--folder-color-green);
  }

  &.folder-color-grey svg {
    color: var(--folder-color-grey);
  }

  &.extra-space-left {
    ${({level}): string =>
      `padding-left: ${
        (level + 1) * TREE_INDENTATION + PLACEHOLDER_SPACE_FOR_ICON
      }px;`};
  }

  &.selected {
    background: var(--color-state-active);
    color: var(--color-core-black);

    .EIconButton {
      color: var(--color-core-black);
    }
  }

  &.disabled {
    cursor: not-allowed;
    opacity: var(--element-disabled-opacity);
  }

  &.is-dragging {
    cursor: grabbing;
    opacity: var(--element-disabled-opacity);
  }

  &.not-selectable {
    cursor: default;
  }

  &:not(.not-selectable):not(.disabled):hover,
  &.is-dragging-over {
    background: var(--color-state-hover);
    color: var(--color-core-black);

    .EIconButton {
      color: var(--color-core-black);

      &:hover {
        color: var(--color-core-white);
      }
    }

    .StyledExtraAction .HeaderLabel svg {
      fill: var(--color-core-black);
    }
  }
`;
